import {crmQueryClient as queryClient} from '@/lib/query/queryClient';
import * as Sentry from "@sentry/react";
import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { initFacebookPixelIfConsented } from "@/lib/fbpixel";
import { onConsentChange } from "@/lib/cookieConsent";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Seo from "@/components/Seo";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminSectionErrorBoundary from "./components/admin/AdminSectionErrorBoundary";
import { LazyOfferRedeemDialog } from "./components/LazyOfferRedeemDialog";
import { MarketingAnalytics } from "./components/MarketingAnalytics";
import ScrollToTop from "./components/ScrollToTop";
import type { ReactElement } from "react";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const RealitkyWebportal = lazy(() => import("./pages/RealitkyWebportal"));
const RestauracieWebsite = lazy(() => import("./pages/RestauracieWebsite"));
const AdvokatiWebsite = lazy(() => import("./pages/AdvokatiWebsite"));
const ZubnaAmbulanciaWebsite = lazy(() => import("./pages/ZubnaAmbulanciaWebsite"));
const FotovoltikaWebsite = lazy(() => import("./pages/FotovoltikaWebsite"));
const GymWebsite = lazy(() => import("./pages/GymWebsite"));
const ZoznamkaWebsite = lazy(() => import("./pages/ZoznamkaWebsite"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Riesenia = lazy(() => import("./pages/Riesenia"));
const RiesenieDetail = lazy(() => import("./pages/RiesenieDetail"));
const Realizacie = lazy(() => import("./pages/Realizacie"));
const RealizacieDetail = lazy(() => import("./pages/RealizacieDetail"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const OchranaSukromia = lazy(() => import("./pages/OchranaSukromia"));
const Vop = lazy(() => import("./pages/Vop"));
const PriebehSpoluprace = lazy(() => import("./pages/PriebehSpoluprace"));
const OSluzbe = lazy(() => import("./pages/OSluzbe"));
const OrderSignature = lazy(() => import("./pages/OrderSignature"));
const ProtectedAdminOutlet = lazy(() =>
  import("./components/admin/rbac/ProtectedAdminOutlet").then(({ ProtectedAdminOutlet }) => ({
    default: ProtectedAdminOutlet,
  })),
);

// FIX-N12: admin routy lazy-loadovane - Suspense + RouteFallback uz existuju (obaluju <Routes>),
// tento balik len presuva 30+ eager admin importov na code-split chunky (bundle size).
const Admin = lazy(() => import("./pages/Admin"));
const AdminToday = lazy(() => import("./pages/AdminToday"));
const AdminCustomer = lazy(() => import("./pages/AdminCustomer"));
const AdminLogs = lazy(() => import("./pages/AdminLogs"));
const AdminProjectNotes = lazy(() => import("./pages/AdminProjectNotes"));
const AdminCommissionsRedirect = lazy(() => import("./pages/AdminCommissionsRedirect"));
const AdminProjects = lazy(() => import("./pages/AdminProjects"));
const AdminProjectDetail = lazy(() => import("./pages/AdminProjectDetail"));
const AdminHostingDetail = lazy(() => import("./pages/AdminHostingDetail"));
const AdminHostingAdminDetail = lazy(() => import("./pages/AdminHostingAdminDetail"));
const AdminPasswords = lazy(() => import("./pages/AdminPasswords"));
const AdminHosting = lazy(() => import("./pages/AdminHosting"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminMarketingDetail = lazy(() => import("./pages/AdminMarketingDetail"));
const AdminClients = lazy(() => import("./pages/AdminClients"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminMyInfo = lazy(() => import("./pages/AdminMyInfo"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminTaskDetail = lazy(() => import("./pages/AdminTaskDetail"));
const AdminTaskUsers = lazy(() => import("./pages/AdminTaskUsers"));
const AdminRentals = lazy(() => import("./pages/AdminRentals"));
const AdminRentalDetail = lazy(() => import("./pages/AdminRentalDetail"));
const AutopayActivation = lazy(() => import("./pages/AutopayActivation"));
const AdminRentalsSuperfakturaAudit = lazy(() => import("./pages/AdminRentalsSuperfakturaAudit"));
const AdminRentalsWebhookIntegrations = lazy(() => import("./pages/AdminRentalsWebhookIntegrations"));
const AdminStatistics = lazy(() => import("./pages/AdminStatistics"));
const AdminSfInvoices = lazy(() => import("./pages/AdminSfInvoices"));
// C7-6: skip AdminDebug chunk in production builds (route redirects below).
const AdminDebug = import.meta.env.DEV ? lazy(() => import("./pages/AdminDebug")) : null;
const AdminSignatures = lazy(() => import("./pages/AdminSignatures"));
const AdminDesigns = lazy(() => import("./pages/AdminDesigns"));
const AdminBlacklist = lazy(() => import("./pages/AdminBlacklist"));
const AdminMonitoring = lazy(() => import("./pages/AdminMonitoring"));
const AdminCommunicationOps = lazy(() => import("./pages/AdminCommunicationOps"));
const AdminRolloutHealth = lazy(() => import("./pages/AdminRolloutHealth"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminFinance = lazy(() => import("./pages/AdminFinance"));
const AdminFinancePayouts = lazy(() => import("./pages/AdminFinancePayouts"));
const AdminFinanceImplementers = lazy(() => import("./pages/AdminFinanceImplementers"));
const AdminFinanceAiTools = lazy(() => import("./pages/AdminFinanceAiTools"));
const AdminFinancePayments = lazy(() => import("./pages/AdminFinancePayments"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminInstructions = lazy(() => import("./pages/AdminInstructions"));

// F18: scopes a render crash to the one admin route instead of the whole
// admin shell / app-wide ErrorBoundary. See AdminSectionErrorBoundary.
function adminSection(title: string, element: ReactElement) {
  return <AdminSectionErrorBoundary title={title}>{element}</AdminSectionErrorBoundary>;
}

// Loads Meta Pixel only once marketing-cookie consent is granted (initial + reactive to a
// mid-session consent change, no reload needed) — see src/lib/fbpixel.ts.
function PixelConsentGate() {
  useEffect(() => {
    initFacebookPixelIfConsented();
    return onConsentChange(() => initFacebookPixelIfConsented());
  }, []);
  return null;
}

function RouteFallback() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </main>
  );
}




function ErrorButton() {
  return (
    <button
      onClick={() => {
        throw new Error("This is your first error!");
      }}
      style={{
        position: "fixed",
        right: "16px",
        bottom: "16px",
        zIndex: 99999,
        background: "#111",
        color: "#fff",
        border: "none",
        borderRadius: "10px",
        padding: "12px 16px",
        cursor: "pointer",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)"
      }}
    >
      Break the world
    </button>
  );
}
const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <PixelConsentGate />
          <MarketingAnalytics />
          <LazyOfferRedeemDialog />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/realitky" element={<RealitkyWebportal />} />
              <Route path="/restauracie" element={<RestauracieWebsite />} />
              <Route path="/advokati" element={<AdvokatiWebsite />} />
              <Route path="/zubna-ambulancia" element={<ZubnaAmbulanciaWebsite />} />
              <Route path="/fotovoltika" element={<FotovoltikaWebsite />} />
              <Route path="/gym" element={<GymWebsite />} />
              <Route path="/zoznamka" element={<ZoznamkaWebsite />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/riesenia" element={<Riesenia />} />
              <Route path="/riesenia/:slug" element={<RiesenieDetail />} />
              <Route path="/realizacie" element={<Realizacie />} />
              <Route path="/realizacie/:slug" element={<RealizacieDetail />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/ochrana-sukromia" element={<OchranaSukromia />} />
              <Route path="/obchodne-podmienky" element={<Vop />} />
              <Route path="/priebeh-spoluprace" element={<PriebehSpoluprace />} />
              <Route path="/o-sluzbe" element={<OSluzbe />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<ProtectedAdminOutlet />}>
                <Route index element={adminSection("Leady", <Admin />)} />
                <Route path="today" element={adminSection("Dnes", <AdminToday />)} />
                <Route path="customer/:customerKey" element={adminSection("Klient", <AdminCustomer />)} />
                <Route path="customers/:customerId" element={adminSection("Klient", <AdminCustomer />)} />
                <Route path="logs" element={adminSection("HistĂłria", <AdminLogs />)} />
                <Route path="communication-ops" element={adminSection("KomunikĂˇcia", <AdminCommunicationOps />)} />
                <Route path="rollout-health" element={adminSection("Stav CRM", <AdminRolloutHealth />)} />
                <Route path="notifications" element={adminSection("Notifikácie", <AdminNotifications />)} />
                <Route path="notes" element={adminSection("PoznĂˇmky", <AdminProjectNotes />)} />
                <Route path="projects" element={adminSection("Projekty", <AdminProjects />)} />
                <Route path="projects/:id" element={adminSection("Projekt", <AdminProjectDetail />)} />
                <Route path="passwords" element={adminSection("HeslĂˇ & sĂşbory", <AdminPasswords />)} />
                <Route path="hosting" element={adminSection("Hosting", <AdminHosting />)} />
                <Route path="hosting/:id" element={adminSection("Hosting", <AdminHostingDetail />)} />
                <Route path="hosting-admin/:id" element={adminSection("Hosting", <AdminHostingAdminDetail />)} />
                <Route path="marketing" element={adminSection("Marketing", <AdminMarketing />)} />
                <Route path="marketing/:id" element={adminSection("Marketing", <AdminMarketingDetail />)} />
                <Route path="clients" element={adminSection("Klienti", <AdminClients />)} />
                <Route path="settings" element={adminSection("Nastavenia", <AdminSettings />)} />
                <Route path="settings/my-info" element={adminSection("Moje info", <AdminMyInfo />)} />
                <Route path="commissions" element={adminSection("Financie", <AdminCommissionsRedirect />)} />
                <Route path="finance" element={adminSection("Financie", <AdminFinance />)} />
                <Route path="finance/payouts" element={adminSection("VĂ˝platy provĂ­ziĂ­", <AdminFinancePayouts />)} />
                <Route
                  path="finance/implementers"
                  element={adminSection("RealizĂˇtori", <AdminFinanceImplementers />)}
                />
                <Route path="finance/ai-tools" element={adminSection("AI nĂˇstroje", <AdminFinanceAiTools />)} />
                <Route path="finance/payments" element={adminSection("Stripe", <AdminFinancePayments />)} />
                <Route path="newsletter" element={adminSection("Newsletter", <AdminNewsletter />)} />
                <Route path="instrukcie" element={adminSection("Inštrukcie", <AdminInstructions />)} />
                <Route path="tasks" element={adminSection("Ăšlohy", <AdminTasks />)} />
                <Route path="tasks/:id" element={adminSection("Ăšloha", <AdminTaskDetail />)} />
                <Route path="task-users" element={adminSection("RealizĂˇtori", <AdminTaskUsers />)} />
                <Route path="rentals" element={adminSection("PrenĂˇjmy", <AdminRentals />)} />
                {/* SF-9: static route BEFORE the :id param route â€” must not be
                    shadowed; React Router v6 ranks static segments over dynamic
                    ones regardless of declaration order, but the explicit order
                    here matches repo convention for readability. */}
                <Route
                  path="rentals/superfaktura-audit"
                  element={adminSection("SuperFaktura audit", <AdminRentalsSuperfakturaAudit />)}
                />
                <Route
                  path="rentals/webhook-integrations"
                  element={adminSection("Webhook integrĂˇcie", <AdminRentalsWebhookIntegrations />)}
                />
                <Route path="rentals/:id" element={adminSection("PrenĂˇjom", <AdminRentalDetail />)} />
                <Route path="statistics" element={adminSection("Ĺ tatistiky", <AdminStatistics />)} />
                <Route path="faktury" element={adminSection("FaktĂşry", <AdminSfInvoices />)} />
                {/* Koleso removed (2026-07-22) â€” keep the old bookmark alive for owner deep links. */}
                <Route
                  path="wheel-leads"
                  element={adminSection("FaktĂşry", <Navigate to="/admin/faktury" replace />)}
                />
                <Route
                  path="debug"
                  element={adminSection(
                    "Debug",
                    AdminDebug ? <AdminDebug /> : <Navigate to="/admin/today" replace />,
                  )}
                />
                <Route path="signatures" element={adminSection("Podpisy", <AdminSignatures />)} />
                <Route path="designs" element={adminSection("Dizajny", <AdminDesigns />)} />
                <Route path="blacklist" element={adminSection("Blacklist", <AdminBlacklist />)} />
                <Route path="monitoring" element={adminSection("Monitoring", <AdminMonitoring />)} />
              </Route>
            {/* Contract-only e-sign â€” dedicated path so a stale /objednavka SPA cannot show the order wizard. */}
            {/* Transactional, personalized flows â€” not meant to be indexed. */}
            <Route
              path="/podpis-zmluvy"
              element={
                <>
                  <Seo title="Podpis zmluvy | Web na prenájom" description="Elektronický podpis zmluvy." path="/podpis-zmluvy" noindex />
                  <OrderSignature forceContractOnly />
                </>
              }
            />
            <Route
              path="/objednavka"
              element={
                <>
                  <Seo title="Objednávka a elektronický podpis | Web na prenájom" description="Objednávka a elektronický podpis zmluvy." path="/objednavka" noindex />
                  <OrderSignature />
                </>
              }
            />
            <Route path="/automaticke-platby" element={<><Seo title="Automatické platby | Web na prenájom" description="Bezpečné nastavenie mesačných platieb." path="/automaticke-platby" noindex/><AutopayActivation/></>}/>
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;


